import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { VIDEO_BASE_URL } from '../config/api';
import { Loader2, Upload, UserRound, GraduationCap, AlignLeft, CheckCircle2, Phone } from 'lucide-react';
import PhoneInput from '../components/ui/PhoneInput';

export default function InstructorOnboarding() {
  const { currentUser, userData, instructorStatus, refreshUserData } = useAuth();
  const navigate = useNavigate();

  const [bio, setBio] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+967');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If they are not logged in, go to login.
  if (!currentUser) return <Navigate to="/login" replace />;

  // If they are already pending or approved, they shouldn't be here
  if (instructorStatus !== 'incomplete') {
    return <Navigate to="/instructor-dashboard" replace />;
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('حجم الصورة يجب أن لا يتجاوز 2MB');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bio.trim() || !specialization.trim() || !phone.trim()) {
      setError('يرجى تعبئة جميع الحقول المطلوبة');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let photoURL = userData?.photoURL || '';

      if (photoFile) {
        const reader = new FileReader();
        const base64Promise = new Promise(resolve => {
          reader.onload = ev => resolve(ev.target.result);
          reader.readAsDataURL(photoFile);
        });
        const base64Data = await base64Promise;

        const uploadRes = await fetch(`${VIDEO_BASE_URL}/api/user/upload-avatar`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: base64Data })
        });
        
        const uploadData = await uploadRes.json();

        if (uploadData.success) {
          photoURL = uploadData.avatarUrl;
        } else {
          throw new Error('فشل رفع الصورة');
        }
      } else if (!photoURL) {
        // Enforce photo for instructors
        setError('يرجى اختيار صورة شخصية احترافية');
        setLoading(false);
        return;
      }

      await updateDoc(doc(db, 'users', currentUser.uid), {
        photoURL,
        phone: `${countryCode} ${phone}`,
        'instructorData.bio': bio,
        'instructorData.specialization': specialization,
        'instructorData.status': 'pending',
      });

      await refreshUserData();
      navigate('/instructor-dashboard'); // This will hit InstructorProtectedRoute and show pending UI
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء حفظ البيانات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex flex-col pt-24 pb-12 px-4 transition-colors">
      <div className="max-w-2xl w-full mx-auto relative z-10">
        
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight mb-3">إكمال الملف الشخصي</h1>
          <p className="text-slate-500 dark:text-zinc-400 font-medium text-lg">لن تكتمل رحلتك معنا إلا بملفك الشخصي الاحترافي.</p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl shadow-2xl p-8 md:p-10 relative overflow-hidden backdrop-blur-xl">
          {/* Ambient Glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/10 dark:bg-primary-500/5 rounded-full blur-[80px] pointer-events-none"></div>

          {error && (
            <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 rounded-2xl font-bold flex gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8" dir="rtl">
            
            {/* Avatar Upload */}
            <div className="flex flex-col items-center justify-center">
              <label htmlFor="photo-upload" className="cursor-pointer group relative">
                <div className={`w-32 h-32 rounded-full border-4 border-slate-100 dark:border-zinc-800 shadow-xl overflow-hidden flex items-center justify-center transition-all ${photoPreview ? 'bg-white' : 'bg-slate-100 dark:bg-zinc-800'} relative z-10`}>
                  {photoPreview ? (
                     <img src={photoPreview} alt="Preview" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  ) : (
                     <UserRound className="w-12 h-12 text-slate-400 dark:text-zinc-500 group-hover:text-primary-500 transition-colors" />
                  )}
                </div>
                <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-white" />
                </div>
              </label>
              <input id="photo-upload" type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
              <p className="text-slate-500 dark:text-zinc-400 mt-4 text-sm font-bold">صورة احترافية (أقل من 2MB)</p>
            </div>

            {/* Specialization */}
            <div>
               <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">التخصص الدقيق</label>
               <div className="relative group">
                 <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                   <GraduationCap className="h-5 w-5 text-slate-400 dark:text-zinc-500 group-focus-within:text-primary-500 transition-colors" />
                 </div>
                 <input
                   type="text"
                   value={specialization}
                   onChange={e => setSpecialization(e.target.value)}
                   className="block text-lg w-full pr-12 pl-4 py-3.5 border border-slate-200 dark:border-white/10 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white transition-all font-medium"
                   placeholder="مثال: مصمم هويات بصرية وUI/UX"
                   required
                 />
               </div>
            </div>

            {/* Phone */}
            <PhoneInput
              phoneValue={phone}
              onPhoneChange={e => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
              countryCodeValue={countryCode}
              onCountryCodeChange={setCountryCode}
              required={true}
            />

            {/* Bio */}
            <div>
               <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">النبذة الشخصية</label>
               <div className="relative group">
                 <div className="absolute top-4 right-0 pr-4 flex items-start pointer-events-none">
                   <AlignLeft className="h-5 w-5 text-slate-400 dark:text-zinc-500 group-focus-within:text-primary-500 transition-colors" />
                 </div>
                 <textarea
                   value={bio}
                   onChange={e => setBio(e.target.value)}
                   rows="4"
                   className="block text-lg w-full pr-12 pl-4 py-3.5 border border-slate-200 dark:border-white/10 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white/50 dark:bg-black/20 text-slate-900 dark:text-white transition-all font-medium resize-none leading-relaxed"
                   placeholder="اكتب نبذة عن خبراتك وإنجازاتك لتظهر للطلاب في صفحتك الشخصية..."
                   required
                 />
               </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-4 px-6 border border-transparent rounded-2xl shadow-xl shadow-primary-500/20 text-xl font-bold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 transition-all hover:-translate-y-0.5 disabled:opacity-70"
            >
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                <>
                  <span>إرسال الطلب للمراجعة</span>
                  <CheckCircle2 className="w-6 h-6" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
